import { useState } from 'react';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import dayjs, { Dayjs } from 'dayjs';
import { unwrapResult } from '@reduxjs/toolkit';

import { CustomAlert, CustomButton, Picker, Text } from '../../../shared/uiComponents';
import { dateNormalizer, errorNormalizer } from '../../../shared/Helpers/functions';
import { useCompareDates } from '../../../shared/Helpers/hooks';
import { useResetCalendarData } from '../Helpers/hooks';
import { TimeInputsWrapper } from '../../Reports/helpers';
import { ButtonWrapper } from '../../../components/StyledComponents';

import { DispatchProperties, useSelector } from '../../../redux/store';
import { setCopyEvent } from '../../../redux/State/clientSlice/calendarHelperSlice';
import { copyEvents } from '../../../redux/State/clientSlice/calendarSlice';
import { CopyEventsProperties } from '../../../redux/API/ClientAPIHelpers/calendarProperties';

const Content = ({ onClose }: { onClose: () => void }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const selectedDay = useSelector((state) => state.calendarHelper.selectedDay);

	const [startValue, setStartValue] = useState<Dayjs>(
		dayjs(`${selectedDay.month + 1}/${selectedDay.day}/${selectedDay.year}`),
	);
	const [endValue, setEndValue] = useState<Dayjs>(
		dayjs(`${selectedDay.month + 1}/${selectedDay.day}/${selectedDay.year}`),
	);
	const [copyToValue, setCopyToValue] = useState<Dayjs | null>(dayjs());

	const { resetData } = useResetCalendarData();
	const { hasError, message } = useCompareDates(startValue, endValue);

	const title = `You are going to copy all events between ${startValue.format(
		'MM-DD-YYYY',
	)} and ${endValue.format('MM-DD-YYYY')} to ${copyToValue ? copyToValue.format('MM-DD-YYYY') : 'MM-DD-YYYY'}`;

	const onCopyConfirmClick = () => {
		if (!startValue || !endValue || !copyToValue) return;
		const startDate = dateNormalizer(startValue);
		const endDate = dateNormalizer(endValue);
		const toDate = dateNormalizer(copyToValue);

		const data: CopyEventsProperties = {
			startDate,
			endDate,
			toDate,
		};

		dispatch(copyEvents(data))
			.then(unwrapResult)
			.then((response) => {
				if (!response?.isSuccessful) {
					toast('Something went wrong. Please contact administation');

					return;
				}
				resetData();
				toast('Done');
				onClose();
			})
			.catch(errorNormalizer);
	};

	const onStartTimeChange = (value: Dayjs | null) => {
		if (!value) return;
		setStartValue(value);
	};

	const onEndTimeChange = (value: Dayjs | null) => {
		if (!value) return;
		setEndValue(value);
	};

	const clearHandler = () => {
		setStartValue(dayjs(`${selectedDay.month}/${selectedDay.day}/${selectedDay.year}`));
		setEndValue(dayjs(`${selectedDay.month}/${selectedDay.day}/${selectedDay.year}`));
		setCopyToValue(null);
	};

	return (
		<>
			<Text title={title} className="marginBottom16" />
			<TimeInputsWrapper>
				<Picker.CustomDate label="Start" value={startValue} onChange={onStartTimeChange} />
				<Picker.CustomDate
					label="End"
					value={endValue}
					onChange={onEndTimeChange}
					error={hasError}
					errorMessage={message}
				/>
			</TimeInputsWrapper>
			<Picker.CustomDate
				label="Copy to"
				value={copyToValue}
				onChange={(date) => setCopyToValue(date)}
				className="marginBottom16"
			/>
			<ButtonWrapper>
				<CustomButton title="Copy" onClick={onCopyConfirmClick} className="marginBottom16" />
				<CustomButton title="Clear" buttonType="secondaryGray" onClick={clearHandler} />
			</ButtonWrapper>
		</>
	);
};

const CopyEvent = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const open = useSelector((state) => state.calendarHelper.popups.copy);

	const closeHandler = () => {
		dispatch(setCopyEvent(false));
	};

	const isCopying = useSelector((state) => state.calendar.loading);

	return (
		<CustomAlert
			isBusy={isCopying}
			open={open}
			title="Copy events"
			onClose={closeHandler}
			Content={Content}
			contentHeight="500px"
		/>
	);
};

export default CopyEvent;
